import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Gift, Plus, Percent, DollarSign, TrendingUp, AlertCircle
} from "lucide-react";
import {
  HeroCard, StatCard, SectionHeader, InsightCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";

const CashbacksTab = () => {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", percentage: 5, min_order: 0, max_cashback: 50 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { authAxiosRef.current.get("/marketing/cashback").then(r => { setRules(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Nombre obligatorio"); return; }
    try { await authAxios.post("/marketing/cashback", form); toast.success("Regla creada"); setShowNew(false); setForm({ name: "", percentage: 5, min_order: 0, max_cashback: 50 }); authAxios.get("/marketing/cashback").then(r => setRules(r.data)); } catch { toast.error("Error"); }
  };

  if (loading) return <LoadingScreen />;

  const activeRules = rules.filter(r => r.active).length;
  const avgCashback = rules.length > 0 ? (rules.reduce((sum, r) => sum + r.percentage, 0) / rules.length).toFixed(1) : 0;
  const maxCashbackTotal = rules.reduce((sum, r) => sum + r.max_cashback, 0);

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Programa de Cashback"
        metric={activeRules}
        subMetrics={[
          {
            label: "Regras Ativas",
            value: `${activeRules}/${rules.length}`,
          },
          {
            label: "Cashback Médio",
            value: `${avgCashback}%`,
          },
        ]}
        color={MARKETING_COLORS.Cashbacks}
      />

      {/* Stats Cards */}
      <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Regras Configuradas"
          value={rules.length.toString()}
          icon={Gift}
          trend="neutral"
          change={0}
        />
        <StatCard
          label="Cashback Médio"
          value={`${avgCashback}%`}
          icon={Percent}
          trend="neutral"
          change={0}
        />
        <StatCard
          label="Cashback Máximo"
          value={`€${maxCashbackTotal.toFixed(2)}`}
          icon={DollarSign}
          trend="neutral"
          change={0}
        />
      </div>

      {/* Overview Banner */}
      <div className="flex gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
        <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
        <span>
          Configure regras de cashback para recompensar clientes frequentes. O cashback é creditado automaticamente e pode ser resgatado em futuras compras.
        </span>
      </div>

      {/* New Rule Button */}
      <div className="flex justify-end">
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2" data-testid="new-cashback-btn">
              <Plus className="w-4 h-4" />
              Criar Nova Regra
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Nova Regra de Cashback</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cashback-name">Nome da Regra</Label>
                <Input
                  id="cashback-name"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Ex: Cashback Natal, Black Friday, Promoção de Verão"
                  data-testid="cashback-name"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cashback-percentage">Porcentagem (%)</Label>
                  <Input
                    id="cashback-percentage"
                    type="number"
                    value={form.percentage}
                    onChange={e => setForm({...form, percentage: Number(e.target.value)})}
                    placeholder="5"
                    data-testid="cashback-percentage"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cashback-min">Pedido Mín. (€)</Label>
                  <Input
                    id="cashback-min"
                    type="number"
                    value={form.min_order}
                    onChange={e => setForm({...form, min_order: Number(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cashback-max">Máx Cashback (€)</Label>
                  <Input
                    id="cashback-max"
                    type="number"
                    value={form.max_cashback}
                    onChange={e => setForm({...form, max_cashback: Number(e.target.value)})}
                    placeholder="50"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="w-full" size="lg" data-testid="create-cashback-btn">
                Criar Regra
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rules List */}
      <SectionHeader
        title="Regras Configuradas"
        subtitle={`${rules.length} regra(s) de cashback ativa(s)`}
      />

      {rules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma regra de cashback configurada. Crie a primeira regra acima!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {rules.map(r => (
            <Card key={r.id} className="overflow-hidden hover:shadow-md transition">
              <CardContent className="flex items-center justify-between p-5">
                <div className="flex items-center gap-4 flex-1">
                  <div className="bg-amber-100 text-amber-600 p-3 rounded-lg shrink-0">
                    <Gift className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm">{r.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.percentage}% cashback • Mín: €{r.min_order} • Máx: €{r.max_cashback}
                    </p>
                  </div>
                </div>
                <Badge className={r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                  {r.active ? "Ativa" : "Inativa"}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Benefits Insights */}
      <SectionHeader
        title="Benefícios do Cashback"
        subtitle="Por que oferecer cashback aos clientes?"
      />

      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type="insight"
          title="Retenção de Clientes"
          message="Clientes com cashback acumulado tendem a retornar para resgatá-lo, aumentando a frequência de compra."
        />
        <InsightCard
          type="success"
          title="Valor Médio da Compra"
          message="Cashback motiva clientes a atingirem valores mínimos, aumentando o ticket médio."
        />
        <InsightCard
          type="alert"
          title="Diferencial Competitivo"
          message="Cashback é um programa simples mas poderoso que diferencia seu negócio da concorrência."
        />
      </div>
    </div>
  );
};

export { CashbacksTab };
